/**
 * Métricas de custo e ROI (T14).
 *
 * Calcula excesso de jornada, rankings por colaborador e segmento, e
 * estimativa conservadora de custo potencial. Nunca afirma "economia
 * garantida" — todo valor monetário é rotulado como simulação.
 *
 * Privacidade: rankings usam AnonymousId como chave. Nenhum CPF/nome circula.
 */

import type {
  CanonicalDataset,
  DailyJourney,
  DiagnosticAssumptions,
} from '../domain/canonical.types';
import type {
  CostMetrics,
  MinuteMetric,
  MoneyEstimate,
  RankedMetric,
  RankedMetricItem,
} from './metrics.types';

// =========================================================================
// Helpers internos
// =========================================================================

/**
 * Número de minutos de excesso para uma jornada (nunca negativo).
 *
 * IMPORTANTE: jornadas com pares incompletos (pelo menos uma marcação
 * sem contrapartida) retornam 0. Não sabemos qual marcação está faltando
 * — podia ser entrada, saída ou pausa, e qualquer comparação contra
 * `expectedMinutes` seria especulativa. Esses dias vão apenas pra
 * "jornadas que precisam de correção".
 *
 * Bug N5/N6: jornadas marcadas como `incompleteData` (par único > 6h, gap
 * intra-jornada > 4h, ou < 50% do esperado em ≤2 pares) também retornam 0.
 * O caso clássico é "esqueceu de bater o ponto do almoço" — par 08:00→17:00
 * vira 9h aparente que NÃO é hora extra real. Sem esse skip, o excesso
 * fictício inflaria custo de hora extra.
 */
export function calculateExcessMinutes(journey: DailyJourney): number {
  if (journey.pairs.some((p) => p.imbalanced)) return 0;
  if (journey.incompleteData) return 0;
  return Math.max(0, journey.workedMinutes - journey.expectedMinutes);
}

/**
 * Resolve o valor-hora efetivo a partir das assumptions.
 * Precedência: hourlyRateBrl > averageGrossSalaryBrl / monthlyHours > monthlyPayrollBrl / monthlyHours.
 * Retorna undefined quando não é possível derivar.
 */
export function resolveHourlyRate(
  assumptions: DiagnosticAssumptions,
  fallbackMonthlyHours?: number,
): number | undefined {
  // 1. Valor-hora direto
  if (assumptions.hourlyRateBrl !== undefined && assumptions.hourlyRateBrl > 0) {
    return assumptions.hourlyRateBrl;
  }

  const monthlyHours =
    fallbackMonthlyHours ??
    (assumptions.monthlyHours > 0 ? assumptions.monthlyHours : 220);

  // 2. Salário bruto médio individual
  if (
    assumptions.averageGrossSalaryBrl !== undefined &&
    assumptions.averageGrossSalaryBrl > 0
  ) {
    return estimateHourlyRateFromPayroll(assumptions.averageGrossSalaryBrl, monthlyHours);
  }

  // 3. Legado: folha mensal total (deprecated — mantido para retrocompatibilidade)
  if (
    assumptions.monthlyPayrollBrl !== undefined &&
    assumptions.monthlyPayrollBrl > 0
  ) {
    return estimateHourlyRateFromPayroll(assumptions.monthlyPayrollBrl, monthlyHours);
  }

  return undefined;
}

// =========================================================================
// Ranking por colaborador
// =========================================================================

/**
 * Agrega excesso de jornada por colaborador e retorna top-10, ordenado
 * do maior para o menor excesso.
 *
 * Chave = AnonymousId do colaborador. Nunca CPF/nome.
 */
export function rankExcessByEmployee(journeys: DailyJourney[]): RankedMetric {
  const totals = new Map<string, number>();

  for (const journey of journeys) {
    const excess = calculateExcessMinutes(journey);
    if (excess > 0) {
      totals.set(journey.employeeId, (totals.get(journey.employeeId) ?? 0) + excess);
    }
  }

  const items: RankedMetricItem[] = Array.from(totals.entries())
    .map(([employeeId, totalExcess]) => ({
      key: employeeId,
      label: employeeId,
      value: totalExcess,
      helper: `${totalExcess} min de potencial excesso no período`,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  return {
    code: 'cost.top_excess_by_employee',
    label: 'Top colaboradores por excesso de jornada',
    helper: 'Colaboradores com maior volume de minutos acima da jornada prevista no período.',
    estimated: false,
    kind: 'ranked',
    items,
  };
}

// =========================================================================
// Ranking por segmento
// =========================================================================

/**
 * Agrega excesso de jornada por segmento do colaborador.
 *
 * Requer dataset com `employees[].segment` preenchido.
 * Retorna null quando nenhum colaborador possui segmento definido.
 */
export function rankExcessBySegment(
  journeys: DailyJourney[],
  dataset: CanonicalDataset | undefined,
): RankedMetric | null {
  if (dataset === undefined) return null;

  // Mapear employeeId → segment
  const segmentMap = new Map<string, string>();
  for (const emp of dataset.employees) {
    if (emp.segment !== undefined && emp.segment.trim().length > 0) {
      segmentMap.set(emp.identity.id, emp.segment);
    }
  }

  if (segmentMap.size === 0) return null;

  // Agregar excesso por segmento
  const totals = new Map<string, number>();

  for (const journey of journeys) {
    const segment = segmentMap.get(journey.employeeId);
    if (segment === undefined) continue;
    const excess = calculateExcessMinutes(journey);
    if (excess > 0) {
      totals.set(segment, (totals.get(segment) ?? 0) + excess);
    }
  }

  if (totals.size === 0) {
    // Há segmentos no dataset, mas nenhum excesso observado
    return {
      code: 'cost.top_excess_by_segment',
      label: 'Excesso de jornada por segmento',
      helper: 'Nenhum excesso de jornada identificado por segmento no período.',
      estimated: false,
      kind: 'ranked',
      items: [],
    };
  }

  const items: RankedMetricItem[] = Array.from(totals.entries())
    .map(([segment, totalExcess]) => ({
      key: segment,
      label: segment,
      value: totalExcess,
      helper: `${totalExcess} min de potencial excesso no período`,
    }))
    .sort((a, b) => b.value - a.value);

  return {
    code: 'cost.top_excess_by_segment',
    label: 'Excesso de jornada por segmento',
    helper: 'Segmentos com maior volume de minutos acima da jornada prevista no período.',
    estimated: false,
    kind: 'ranked',
    items,
  };
}

// =========================================================================
// Estimativa de custo
// =========================================================================

/**
 * Estima o custo de horas extras a partir de um valor-hora e minutos excedentes.
 *
 * O resultado é sempre marcado como estimativa (estimated: true).
 * Linguagem conservadora: "custo potencial", nunca "economia garantida".
 */
export function estimateCostFromHourlyRate(
  minutes: number,
  hourlyRate: number,
): MoneyEstimate {
  const hours = minutes / 60;
  const value = hours * hourlyRate;

  return {
    code: 'cost.estimated_excess_cost',
    label: 'Custo potencial de excesso de jornada',
    helper:
      'Simulação baseada no valor-hora informado. Não representa economia garantida.',
    assumptionsUsed: [`valor-hora: R$ ${hourlyRate.toFixed(2)}`],
    estimated: true,
    kind: 'money',
    value,
  };
}

/**
 * Estima o valor-hora médio a partir do salário/folha mensal e horas mensais esperadas.
 *
 * Retorna o valor-hora (BRL) sem arredondamento, para uso intermediário.
 */
export function estimateHourlyRateFromPayroll(
  monthlySalary: number,
  expectedMonthlyHours: number,
): number {
  if (expectedMonthlyHours <= 0) return 0;
  return monthlySalary / expectedMonthlyHours;
}

// =========================================================================
// Helpers de período (usados pela função principal)
// =========================================================================

/**
 * Calcula o número de dias de calendário entre duas datas (inclusive).
 * Retorna pelo menos 1 para evitar divisão por zero.
 */
export function calcPeriodDays(start: string, end: string): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  const days = Math.round((endMs - startMs) / msPerDay) + 1;
  return Math.max(1, days);
}

// =========================================================================
// Função principal
// =========================================================================

/**
 * Calcula todas as métricas de custo a partir das jornadas diárias.
 *
 * - `estimatedExcessCost`/`costOvertime` é null quando as assumptions não fornecem
 *   hourlyRateBrl nem averageGrossSalaryBrl (nem monthlyPayrollBrl legado).
 * - `costAtrasos` estima o custo dos minutos perdidos em atrasos.
 * - `costAbsences` estima o custo das faltas REGULARES (isoladas, ≤4 dias
 *   consecutivos). Quando o caller passa `leaveBlocksDays` separadamente,
 *   `unjustifiedAbsencesInferredDays` deve representar APENAS faltas isoladas
 *   (= total − leaveBlocksDays). Caso `leaveBlocksDays` seja `undefined`,
 *   mantém-se o comportamento legado: o caller passa o total e tudo entra
 *   em `costAbsences`.
 * - `costLeaveBlocks` estima o custo INFORMATIVO de blocos de afastamento
 *   (atestado/licença/férias). NÃO é somado em `costTotal` — é benefício
 *   pago por lei, irrecuperável.
 * - `costCorrection` estima o custo das correções (esforço de DP em ajustes
 *   manuais + pares imbalanced + marcações desconsideradas).
 * - `costTotal` é a soma de costAtrasos + costOvertime + costAbsences +
 *   costCorrection. Não inclui `costLeaveBlocks`.
 */
export function calculateCostMetrics(
  journeys: DailyJourney[],
  assumptions: DiagnosticAssumptions,
  dataset?: CanonicalDataset,
  reworkCorrectionMinutes?: number,
  unjustifiedAbsencesInferredDays?: number,
  leaveBlocksDays?: number,
): CostMetrics {
  // 1. Total de minutos excedentes (apenas positivos)
  let totalExcess = 0;
  for (const j of journeys) {
    totalExcess += calculateExcessMinutes(j);
  }

  // 1b. Saldo bruto algébrico e déficit absoluto (E2 — narrativa equilibrada).
  // grossBalance soma (worked − expected) com sinal — só inclui jornadas com
  // scheduledDay e pares balanceados. deficit soma só os minutos abaixo do
  // esperado, exigindo pelo menos uma marcação no dia (mirror do excesso).
  let totalGrossBalance = 0;
  let totalDeficit = 0;
  for (const j of journeys) {
    if (!j.scheduledDay) continue;
    if (j.pairs.some((p) => p.imbalanced)) continue;
    const delta = j.workedMinutes - j.expectedMinutes;
    totalGrossBalance += delta;
    if (j.workedMinutes > 0 && delta < 0) {
      totalDeficit += -delta;
    }
  }

  const distinctEmployees = new Set(journeys.map(j => j.employeeId)).size;

  // Número de jornadas-dia ativas: conta apenas dias em que o colaborador
  // efetivamente marcou ponto.
  const activeJourneyDays = journeys.length;

  // Dias de calendário do período analisado (referência pra normalizar mensal).
  const periodDays = dataset !== undefined
    ? calcPeriodDays(dataset.period.start, dataset.period.end)
    : 0;

  const excessMinutes: MinuteMetric = {
    code: 'cost.excess_minutes',
    label: 'Total de minutos acima da jornada prevista',
    helper: 'Soma dos minutos excedentes em relação à jornada esperada. Não implica obrigação de pagamento.',
    estimated: false,
    kind: 'minutes',
    value: totalExcess,
  };

  const grossBalanceMinutes: MinuteMetric = {
    code: 'cost.gross_balance_minutes',
    label: 'Saldo bruto (worked − expected)',
    helper:
      'Soma algébrica entre o que foi trabalhado e o esperado, em todas as jornadas — positivo ou negativo. Difere do excesso porque excessos de uns podem cancelar déficits de outros (compensação só vale onde a CCT permite).',
    estimated: false,
    kind: 'minutes',
    value: totalGrossBalance,
  };

  const deficitMinutes: MinuteMetric = {
    code: 'cost.deficit_minutes',
    label: 'Total de minutos abaixo da jornada prevista',
    helper:
      'Soma dos minutos em que o colaborador trabalhou menos que o esperado, com pelo menos uma marcação no dia. Espelha o excesso e mostra o "outro lado" do saldo.',
    estimated: false,
    kind: 'minutes',
    value: totalDeficit,
  };

  const perEmployeePeriod = distinctEmployees > 0 ? totalExcess / distinctEmployees : 0;

  const excessMinutesPerEmployee: MinuteMetric = {
    code: 'cost.excess_minutes_per_employee',
    label: 'Média de minutos excedentes por colaborador (período)',
    helper: 'Total de minutos excedentes dividido pelo número de colaboradores com jornadas no período analisado.',
    estimated: false,
    kind: 'minutes',
    value: perEmployeePeriod,
  };

  const perEmployeePerDay =
    activeJourneyDays > 0 ? totalExcess / activeJourneyDays : 0;

  const excessMinutesPerEmployeePerDay: MinuteMetric = {
    code: 'cost.excess_minutes_per_employee_per_day',
    label: 'Média diária de minutos excedentes por jornada',
    helper: 'Excesso total dividido pelo número de jornadas-dia registradas (apenas dias com marcação de ponto).',
    estimated: false,
    kind: 'minutes',
    value: perEmployeePerDay,
  };

  const dailyHours = assumptions.dailyHours > 0 ? assumptions.dailyHours : 8;
  const monthlyHours = assumptions.monthlyHours > 0 ? assumptions.monthlyHours : 220;

  // Mensal: extrapola o total por colaborador do período pra 30 dias calendário.
  // Quando o período tem ~30 dias o resultado fica próximo do "por colaborador
  // (período)" — propriedade que o usuário esperava, e que a fórmula antiga
  // (perEmployeePerDay × workDaysPerMonth) inflava porque assumia 27,5 dias
  // úteis num período que pode ter qualquer duração.
  const monthlyMultiplier = periodDays > 0 ? 30 / periodDays : 0;
  const excessMinutesPerEmployeePerMonth: MinuteMetric = {
    code: 'cost.excess_minutes_per_employee_per_month',
    label: 'Média mensal de minutos excedentes por colaborador',
    helper: periodDays > 0
      ? `Extrapolação para 30 dias: total por colaborador no período × (30 ÷ ${periodDays} dias do período).`
      : 'Extrapolação para 30 dias: requer período definido no dataset.',
    assumptionsUsed: [
      `periodDays: ${periodDays}`,
    ],
    estimated: true,
    kind: 'minutes',
    value: perEmployeePeriod * monthlyMultiplier,
  };

  // 2. Rankings
  const topExcessByEmployee = rankExcessByEmployee(journeys);
  const topExcessBySegment = rankExcessBySegment(journeys, dataset);

  // 3. Resolver valor-hora efetivo (leva em conta hierarquia de assumptions)
  // Para calcular custo via payroll legado, precisamos das horas esperadas do dataset
  const totalExpectedHoursFromJourneys = journeys.reduce(
    (acc, j) => acc + j.expectedMinutes / 60,
    0,
  );
  const effectiveMonthlyHoursForRate =
    totalExpectedHoursFromJourneys > 0 ? totalExpectedHoursFromJourneys : monthlyHours;

  let effectiveHourlyRate: number | undefined;
  let rateAssumptionLabel: string;

  if (assumptions.hourlyRateBrl !== undefined && assumptions.hourlyRateBrl > 0) {
    effectiveHourlyRate = assumptions.hourlyRateBrl;
    rateAssumptionLabel = `valor-hora: R$ ${assumptions.hourlyRateBrl.toFixed(2)}`;
  } else if (
    assumptions.averageGrossSalaryBrl !== undefined &&
    assumptions.averageGrossSalaryBrl > 0
  ) {
    effectiveHourlyRate = estimateHourlyRateFromPayroll(
      assumptions.averageGrossSalaryBrl,
      monthlyHours,
    );
    rateAssumptionLabel = `salário bruto médio: R$ ${assumptions.averageGrossSalaryBrl.toFixed(2)}`;
  } else if (
    assumptions.monthlyPayrollBrl !== undefined &&
    assumptions.monthlyPayrollBrl > 0
  ) {
    // Legado: folha mensal total
    effectiveHourlyRate = estimateHourlyRateFromPayroll(
      assumptions.monthlyPayrollBrl,
      effectiveMonthlyHoursForRate,
    );
    rateAssumptionLabel = `folha mensal: R$ ${assumptions.monthlyPayrollBrl.toFixed(2)}`;
  } else {
    rateAssumptionLabel = '';
  }

  // 4. Estimativa de custo de horas extras (estimatedExcessCost / costOvertime)
  let estimatedExcessCost: MoneyEstimate | null = null;
  let costOvertime: MoneyEstimate | null = null;

  if (effectiveHourlyRate !== undefined && effectiveHourlyRate > 0) {
    const base = estimateCostFromHourlyRate(totalExcess, effectiveHourlyRate);
    const sharedAssumptions = [
      rateAssumptionLabel,
      `valor-hora derivado: R$ ${effectiveHourlyRate.toFixed(2)}`,
      'simulação — não representa obrigação legal',
    ].filter(Boolean);

    estimatedExcessCost = {
      ...base,
      assumptionsUsed: sharedAssumptions,
    };

    costOvertime = {
      ...base,
      code: 'cost.overtime_cost',
      label: 'Custo das Horas Extras',
      helper: 'Custo estimado das horas trabalhadas além da jornada prevista.',
      assumptionsUsed: sharedAssumptions,
    };
  }

  // 5. Custo das jornadas mais curtas (déficit de minutos vs. escala)
  //    Pula jornadas com pares incompletos: nesse caso, não dá pra inferir
  //    se faltou marcação (entrada/saída/intervalo) ou se de fato a jornada
  //    foi mais curta — o déficit aí seria fictício e inflaria o cálculo.
  let costAtrasos: MoneyEstimate | null = null;
  if (effectiveHourlyRate !== undefined && effectiveHourlyRate > 0) {
    const toleranceMinutes = assumptions.toleranceMinutes ?? 10;
    let totalShortMinutes = 0;

    for (const journey of journeys) {
      if (!journey.scheduledDay) continue;
      if (journey.workedMinutes === 0) continue;
      // Dados parciais: o déficit reflete batidas faltando, não atraso real.
      if (journey.incompleteData) continue;
      // Marcações ímpares: não é possível calcular déficit de forma confiável.
      if (journey.pairs.some((p) => p.imbalanced)) continue;
      const deficit = journey.expectedMinutes - journey.workedMinutes;
      if (deficit > toleranceMinutes) {
        totalShortMinutes += deficit;
      }
    }

    const shortValue = (totalShortMinutes / 60) * effectiveHourlyRate;
    costAtrasos = {
      code: 'cost.atrasos_cost',
      label: 'Custo das jornadas mais curtas',
      helper: 'Custo estimado dos minutos faltantes em jornadas que terminaram abaixo da escala prevista (descontada a tolerância). Ignora dias com marcações ímpares.',
      assumptionsUsed: [
        rateAssumptionLabel,
        `valor-hora derivado: R$ ${effectiveHourlyRate.toFixed(2)}`,
        `tolerância: ${toleranceMinutes} min`,
        'simulação — não representa obrigação legal',
      ].filter(Boolean),
      estimated: true,
      kind: 'money',
      value: shortValue,
    };
  }

  // 6. Custo das ausências (faltas REGULARES — isoladas, ≤4 dias consecutivos)
  //    e custo INFORMATIVO de blocos de afastamento (≥5 dias = atestado/
  //    licença/férias).
  //
  //    Quando `leaveBlocksDays` é passado, `unjustifiedAbsencesInferredDays`
  //    é o total. Aqui derivamos:
  //      regularDays = total − leaveBlocksDays    (entra em costAbsences)
  //      leaveDays   = leaveBlocksDays            (entra em costLeaveBlocks)
  //
  //    Se `leaveBlocksDays` for undefined, comportamento legado: tudo entra
  //    em costAbsences e costLeaveBlocks fica zerado/null.
  let costAbsences: MoneyEstimate | null = null;
  let costLeaveBlocks: MoneyEstimate | null = null;
  if (effectiveHourlyRate !== undefined && effectiveHourlyRate > 0) {
    const totalCount = unjustifiedAbsencesInferredDays ?? 0;
    const leaveCount = leaveBlocksDays ?? 0;
    // Defesa: nunca permitir regularCount negativo se o caller passar
    // leaveBlocksDays > total por engano.
    const regularCount = Math.max(0, totalCount - leaveCount);

    const regularMinutes = regularCount * dailyHours * 60;
    const regularValue = (regularMinutes / 60) * effectiveHourlyRate;

    costAbsences = {
      code: 'cost.absences_cost',
      label: 'Custo das Faltas',
      helper:
        `${regularCount} dia(s) de falta isolada (run < 5 dias úteis consecutivos) — ` +
        `cada dia valorado pelo valor-hora × jornada padrão (${dailyHours}h). NÃO inclui ` +
        `blocos de afastamento (5+ dias úteis consecutivos = provável atestado/licença/` +
        `férias, que é benefício pago por lei e aparece em "Custo Informativo de ` +
        `Afastamentos").`,
      assumptionsUsed: [
        rateAssumptionLabel,
        `valor-hora derivado: R$ ${effectiveHourlyRate.toFixed(2)}`,
        `dailyHours: ${dailyHours}h`,
        'simulação — não representa obrigação legal',
      ].filter(Boolean),
      estimated: true,
      kind: 'money',
      value: regularValue,
    };

    const leaveMinutes = leaveCount * dailyHours * 60;
    const leaveValue = (leaveMinutes / 60) * effectiveHourlyRate;
    costLeaveBlocks = {
      code: 'cost.leave_blocks_cost',
      label: 'Custo Informativo de Afastamentos',
      helper:
        `${leaveCount} dia(s) em blocos de afastamento (5+ dias úteis consecutivos sem ` +
        `ponto, dentro da janela de presença do colaborador). Provável atestado/licença/` +
        `férias — é benefício pago por lei (INSS/empresa), NÃO custo recuperável. Exibido ` +
        `apenas como referência e NÃO somado no Custo Total.`,
      assumptionsUsed: [
        rateAssumptionLabel,
        `valor-hora derivado: R$ ${effectiveHourlyRate.toFixed(2)}`,
        `dailyHours: ${dailyHours}h`,
        'informativo — não recuperável, não somado no Custo Total',
      ].filter(Boolean),
      estimated: true,
      kind: 'money',
      value: leaveValue,
    };
  }

  // 7. Custo das correções
  let costCorrection: MoneyEstimate | null = null;
  if (effectiveHourlyRate !== undefined && effectiveHourlyRate > 0) {
    const corrMinutes = reworkCorrectionMinutes ?? 0;
    const corrValue = (corrMinutes / 60) * effectiveHourlyRate;
    costCorrection = {
      code: 'cost.correction_cost',
      label: 'Custo das Correções',
      helper: 'Custo estimado do esforço administrativo de correção manual de marcações.',
      assumptionsUsed: [
        rateAssumptionLabel,
        `valor-hora derivado: R$ ${effectiveHourlyRate.toFixed(2)}`,
        'simulação — não representa obrigação legal',
      ].filter(Boolean),
      estimated: true,
      kind: 'money',
      value: corrValue,
    };
  }

  // 8. Custo total
  let costTotal: MoneyEstimate | null = null;
  let costTotalPerEmployee: MoneyEstimate | null = null;
  if (
    costAtrasos !== null &&
    costOvertime !== null &&
    costAbsences !== null &&
    costCorrection !== null
  ) {
    const totalValue =
      costAtrasos.value + costOvertime.value + costAbsences.value + costCorrection.value;
    costTotal = {
      code: 'cost.total_cost',
      label: 'Custo Total Estimado',
      helper: 'Soma dos custos estimados de jornadas mais curtas, horas extras, faltas e correções manuais.',
      assumptionsUsed: [
        rateAssumptionLabel,
        'simulação — não representa obrigação legal',
      ].filter(Boolean),
      estimated: true,
      kind: 'money',
      value: totalValue,
    };

    const perEmployeeValue = distinctEmployees > 0 ? totalValue / distinctEmployees : 0;
    costTotalPerEmployee = {
      code: 'cost.total_cost_per_employee',
      label: 'Custo Total por Colaborador',
      helper: `Custo total estimado dividido pelos ${distinctEmployees} colaboradores ativos no período.`,
      assumptionsUsed: [
        rateAssumptionLabel,
        `colaboradores ativos: ${distinctEmployees}`,
        'simulação — não representa obrigação legal',
      ].filter(Boolean),
      estimated: true,
      kind: 'money',
      value: perEmployeeValue,
    };
  }

  return {
    excessMinutes,
    grossBalanceMinutes,
    deficitMinutes,
    excessMinutesPerEmployee,
    excessMinutesPerEmployeePerDay,
    excessMinutesPerEmployeePerMonth,
    topExcessByEmployee,
    topExcessBySegment,
    estimatedExcessCost,
    costAtrasos,
    costOvertime,
    costAbsences,
    costLeaveBlocks,
    costCorrection,
    costTotal,
    costTotalPerEmployee,
  };
}
